import {
    removeInvisibleCharsOnPaste,
    updateControl, validateControl
} from '../../../../../../domain/validation/validation';
import React, { ChangeEvent, ClipboardEvent, FocusEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { IFormControl } from '../../../../../../ui/forms/interfaces';
import FormTextField from '../../../../../../ui/forms/fields/text-field';
import { getSettingFromHtml } from '../../../../../../domain/common';
import { newCreditCardActions, rootActions, useAppDispatch, useAppSelector } from '../../../data/store';
import { INewCreditCardEvent } from '../../../interfaces';
import { CreditCardTabTabsEnum } from '../../../enums/credit-card-tab';
import { checkIsSubscription, getZoozSecureFieldsError } from '../../../domain/shopping-cart-provider';
import InfoIcon from '../../../../../../ui/svg/common/info';
import RememberMePopup from './remember-me-popup';

/**
 * https://developers.paymentsos.com/docs/getting-started/collecting-card-information/using-the-secure-fields-form.html
 * https://developers.paymentsos.com/apis-and-sdks/client-library-references/securefieldsreference.html
 */
const NewCreditCard = () => {

    const dispatch = useAppDispatch();
    const savedCreditCards = useAppSelector(store => store.savedCreditCard.savedCreditCards);
    const rememberMe = useAppSelector(store => store.newCreditCard.rememberMe);

    const nameOnCardControl = useAppSelector(store => store.newCreditCard.nameOnCardControl);
    const zoozFieldError = useAppSelector(store => store.newCreditCard.zoozFieldError);
    const [zoozFieldFocused, setZoozFieldFocused] = useState(false);

    // info popups
    const rememberMePopupShown = useAppSelector(store => store.newCreditCard.rememberMePopupShown);

    const cartData = useAppSelector(store => store.root.cartData);
    const isSubscription = checkIsSubscription(cartData);

    /**
     * update zooz field error
     * later, it may be used in confirm order
     */
    const setZoozFieldError = useCallback((error: string) => {
        dispatch(
            newCreditCardActions.main({
                zoozFieldError: error,
            })
        );
    }, [dispatch]);

    /**
     * zooz field client side validation
     */
    const validateZoozField = useCallback((evt: INewCreditCardEvent) => {

        setZoozFieldError('');

        if(!evt) return;

        if(evt.empty){
            setZoozFieldError('This field is required');
            return;
        }

        if(!evt.error) return;

        setZoozFieldError(getZoozSecureFieldsError(evt));
    }, [setZoozFieldError]);

    /**
     * init zooz 'secure fields'
     * https://developers.paymentsos.com/docs/getting-started/collecting-card-information/using-the-secure-fields-form.html
     */
    useEffect(() => {
        const publicKey = getSettingFromHtml('zooz-business-unit-public-key');
        if(!publicKey) return;

        // create an instance of the secure fields. Pass the public key as the first argument
        const formElements = new window.POS.Fields(publicKey, {});

        // the placeholders object is just one example of an additional option you can pass when instantiating a field
        const placeholders = {
            cardNumber: 'Enter Card Number',
            expDate: 'MM / YY'
        }

        // create an instance of each field you want to show in your form, by calling formElements.create(elementType,options?)
        // with the type of field you want to instantiate and any additional options you want pass in.
        const zoozSecureFields = formElements.create('fullCreditCard', {
            placeholders
        });

        // these events are used to style a border on focus
        // and to print an error
        zoozSecureFields.on('focus', (evt: INewCreditCardEvent) => {
            setZoozFieldFocused(true);
        });
        zoozSecureFields.on('blur', (evt: INewCreditCardEvent) => {
            setZoozFieldFocused(false);
            validateZoozField(evt);
        });
        zoozSecureFields.on('change', (evt: INewCreditCardEvent) => {
            if(evt && !evt.empty){
                setZoozFieldError('');
            }
        });

        // mount each field to the DOM
        zoozSecureFields.mount('#secure-secure-fields')
        dispatch(
            newCreditCardActions.main({
                zoozSecureFields,
            })
        );

        return () => {
            if(zoozSecureFields){
                zoozSecureFields.destroy();
            }
        };

    }, [dispatch, setZoozFieldError, validateZoozField]);

    /**
     * On input change
     */
    const handleChangeEvent = (evt: ChangeEvent, control: IFormControl, controlName: string) => {
        const $target = evt.target as HTMLInputElement;

        dispatch(
            newCreditCardActions.setControl({
                controlName,
                control: updateControl(control, $target.value, !control.isValid)
            })
        );
    };

    /**
     * remove invisible characters on paste event
     */
    const handlePasteEvent = (evt: ClipboardEvent<HTMLInputElement>, control: IFormControl, controlName: string) => {

        dispatch(
            newCreditCardActions.setControl({
                controlName,
                control: updateControl(control,  removeInvisibleCharsOnPaste(evt), !control.isValid)
            })
        );
    };

    /**
     * handle key up event
     */
    const handleKeyUp = (evt: KeyboardEvent, control: IFormControl, controlName: string) => {

        if(evt.key === 'Escape') {
            dispatch(
                newCreditCardActions.setControl({
                    controlName,
                    control: updateControl(control, '', !control.isValid)
                })
            );
        }
    };

    /**
     * validate on blur
     */
    const handleBlurEvent = (evt: FocusEvent, control: IFormControl, controlName: string) => {
        dispatch(
            newCreditCardActions.setControl({
                controlName,
                control: validateControl(control),
            })
        );
    };

    /**
     * on 'My saved cards' button click
     */
    const handleSavedCardsButton = () => {
        dispatch(
            rootActions.main({
                selectedCreditCardTab: CreditCardTabTabsEnum.SavedCreditCards,
            })
        );
    };

    const handleRememberMeChange = (evt: ChangeEvent<HTMLInputElement>) => {
        dispatch(
            newCreditCardActions.main({
                rememberMe: evt.target.checked,
            })
        )
    };

    /**
     * show 'remember me' info popup
     */
    const rememberMePopupInfoClicked = () => {
        dispatch(
            newCreditCardActions.main({
                rememberMePopupShown: true,
            })
        );
    };

    /**
     * show cvv info popup
     */
    const cvvInfoPopupInfoClicked = () => {
        dispatch(
            newCreditCardActions.main({
                cvvInfoPopupShown: true,
            })
        );
    };

    return (
        <div>
            {
                savedCreditCards && savedCreditCards.length > 0 &&
                <button type="button" className="text-tiny font-bold text-blue-200 mb-4" onClick={ handleSavedCardsButton }>
                    My Saved Cards
                </button>
            }

            <FormTextField
                id={ 'nameOnCard' }
                label={ 'Name on Card' }
                control={ nameOnCardControl }
                maxLength={ 100 }
                rootClasses="mb-0"
                qa={ 'cart-step3-card-holder-name-tb' }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, nameOnCardControl, 'nameOnCardControl');
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, nameOnCardControl, 'nameOnCardControl');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, nameOnCardControl, 'nameOnCardControl');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, nameOnCardControl, 'nameOnCardControl');
                }}
            />

            <div className="flex items-center justify-end mb-2">
                <button
                    type="button"
                    className="text-sm text-gray-500 flex items-center"
                    onClick={ cvvInfoPopupInfoClicked }>
                    <span className="mr-2">What is CVV?</span>
                    <InfoIcon width="15px" color="#777" />
                </button>
            </div>

            <div
                id="zooz-secure-fields"
                className={`grid w-full ${ zoozFieldFocused ? 'zooz-field-focused' : '' } ${ zoozFieldError ? 'zooz-field-error' : '' }`}>

                <div className="w-form-field">
                    <div className="w-form-tb rounded" id="secure-secure-fields" />
                </div>

                {
                    zoozFieldError &&
                    <div className="text-red text-sm">{ zoozFieldError }</div>
                }
            </div>

            <label className={`flex items-center text-gray-500 mt-2 ${ isSubscription ? 'hidden' : '' }`}>
                <input
                    type="checkbox"
                    checked={ isSubscription ? true : rememberMe }
                    onChange={ handleRememberMeChange }
                />

                <span className="mx-2">Remember me</span>
                <button type="button" onClick={ rememberMePopupInfoClicked }>
                    <InfoIcon width="15px" color="#777" />
                </button>
            </label>

            { rememberMePopupShown && <RememberMePopup /> }
        </div>
    )
};

export default NewCreditCard;